import { useEffect, useState } from "react";
import type { MeResponse } from "../api/me";
import { logout } from "../api/auth";
import { SOUND_CHANGED_EVENT, isSoundOnDone, setSoundOnDone } from "../lib/chime";

const DISMISS_KEY = "thunderay-ai:email-verify-dismissed";

/**
 * A small bell toggle for the "play a sound when the prompt is done" preference.
 * Reads the stored value on mount and stays in sync with other mounts / tabs via
 * the `thunderay-ai:sound-changed` event bus (and `storage` for foreign tabs).
 */
function SoundToggle() {
  const [on, setOn] = useState(isSoundOnDone);
  useEffect(() => {
    const sync = () => setOn(isSoundOnDone());
    window.addEventListener(SOUND_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SOUND_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return (
    <button
      type="button"
      className={`topbar-sound${on ? " active" : ""}`}
      aria-pressed={on}
      aria-label={on ? "Disable sound when a response finishes" : "Enable sound when a response finishes"}
      title={on ? "Sound when done: on — click to mute" : "Sound when done: off — click to enable"}
      onClick={() => setSoundOnDone(!isSoundOnDone())}
    >
      {on ? (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8.7 3A6 6 0 0 1 18 8c0 .7.1 1.4.2 2" />
          <path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          <line x1="2" y1="2" x2="22" y2="22" />
        </svg>
      )}
    </button>
  );
}

function EmailVerifyBanner({ email }: { email: string }) {
  const [dismissed, setDismissed] = useState(
    typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY) === email,
  );
  if (dismissed) return null;
  return (
    <div className="topbar-banner" role="status">
      <span>
        Your AI-made commits will show as <strong>unverified</strong> on GitHub until you add{" "}
        <code>{email}</code> at{" "}
        <a
          href="https://github.com/settings/emails"
          target="_blank"
          rel="noopener noreferrer"
        >
          github.com/settings/emails
        </a>
        .
      </span>
      <button
        type="button"
        onClick={() => {
          try { localStorage.setItem(DISMISS_KEY, email); } catch {}
          setDismissed(true);
        }}
      >
        Got it
      </button>
    </div>
  );
}

type Props = {
  me: MeResponse | null;
  loading: boolean;
  error: Error | null;
  onToggleSidebar?: () => void;
  onManageUsers?: () => void;
  onOpenQa?: () => void;
  onOpenCmdk?: () => void;
};

async function handleSignOut(externalUrl: string | null) {
  if (externalUrl) {
    window.location.href = externalUrl;
    return;
  }
  try {
    await logout();
  } catch {
    /* ignore — still reload to drop client state */
  }
  window.location.reload();
}

function Identity({ me, loading, error }: Props) {
  if (loading) {
    return <span className="topbar-loading" aria-live="polite">Loading…</span>;
  }
  if (error) {
    if (error.message === "unauthorized") {
      return <span className="topbar-unauth">Sign-in required</span>;
    }
    return <span className="topbar-error" title={error.message}>Identity unavailable</span>;
  }
  if (me) {
    return (
      <>
        <span className="topbar-email">{me.email}</span>
        <span className="topbar-divider" aria-hidden="true"> · </span>
        <span className={`topbar-role topbar-role-${me.role}`}>{me.role}</span>
      </>
    );
  }
  return null;
}

export default function TopBar({ me, loading, error, onToggleSidebar, onManageUsers, onOpenQa, onOpenCmdk }: Props) {
  return (
    <>
      <header className="topbar" role="banner">
        {onToggleSidebar && (
          <button
            type="button"
            className="topbar-hamburger"
            aria-label="Toggle navigation"
            onClick={onToggleSidebar}
          >
            ☰
          </button>
        )}
        <h1>ThunderAy AI Workspace</h1>
        {onOpenCmdk && me && (
          <button
            type="button"
            className="topbar-cmdk"
            onClick={onOpenCmdk}
            aria-label="Open command palette"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3-3" />
            </svg>
            Search projects, issues…
            <kbd>⌘K</kbd>
          </button>
        )}
        <div className="topbar-identity">
          <SoundToggle />
          <Identity me={me} loading={loading} error={error} />
          {me && onOpenQa && (
            <button type="button" className="topbar-manage" onClick={onOpenQa}>
              QA tests
            </button>
          )}
          {me?.role === "admin" && onManageUsers && (
            <button type="button" className="topbar-manage" onClick={onManageUsers}>
              Manage
            </button>
          )}
          {me && (
            <button
              type="button"
              className="topbar-signout"
              onClick={() => void handleSignOut(me.signOutUrl)}
            >
              Sign out
            </button>
          )}
        </div>
      </header>
      {me && <EmailVerifyBanner email={me.email} />}
    </>
  );
}
